import { Alert, Box, Fade, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useState } from "react";
import {
  AutocompleteInput,
  Button,
  List,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectColumnsButton,
  SelectInput,
  TextField,
  TopToolbar,
  useListFilterContext,
} from "react-admin";
import { useStore } from "react-admin";
import { useFormContext } from "react-hook-form";
import { atom, useRecoilState } from "recoil";
import { grants } from "./backoffice.access_control.js";
import { ExcelExportButton } from "./buttons/ExcelExportButton.js";
import { MoreFiltersButton } from "./buttons/MoreFiltersButton";

import { PerformanceOverviewRecordDialog } from "./dialogs/PerformanceOverviewRecordDialog";
import { PerformanceOverviewReportExportDialog } from "./dialogs/PerformanceOverviewReportExportDialog.js";
import { DateField } from "./fields/DateField";
import { MoneyField } from "./fields/MoneyField";
import { PAYMENT_STATUS_LABEL_AND_COLOR, PaymentStatusField } from "./fields/PaymentStatusField";
import { formatPercentage, PercentageField } from "./fields/PercentageField";
import { useDialog } from "./hooks/useDialog.js";
import { useMenu } from "./hooks/useMenu";
import { ServiceTimeFilter } from "./inputs/ServiceTimeFilter";
import { PerformanceOverviewDataGrid } from "./lists/PerformanceOverviewDataGrid";
import { EmptyState } from "./misc/EmptyState";
import { LoadingIndicator } from "./misc/LoadingIndicator";
import { formatMoney } from "./misc/Money.js";
import { ResponsiveListSurface } from "./misc/ResponsiveListSurface";
import type { AutovioPayoutRecord } from "./providers/autovioPayoutProvider.js";
import { DrivingSchool, isDrivingSchool } from "./providers/drivingSchoolsProvider";
import {
  performanceOverviewProvider,
  PerformanceOverviewRecord,
  type PerformanceOverviewPerformanceType,
} from "./providers/performanceOverviewProvider";
import { Student } from "./providers/studentsProvider.js";
import { PerformanceOverviewEntryEndpoint } from "./generated/backendClient/index.js";

interface ColumnConfig {
  withStudentColumn: boolean;
  withCompensationColumns: boolean;
  withTakeRateColumn: boolean;
  context: DrivingSchool | Student;
}

const PERFORMANCE_TYPE_CHOICES: Array<{ id: PerformanceOverviewPerformanceType; name: string }> = [
  { id: "baseFee", name: "Grundbeträge" },
  { id: "drivingLesson", name: "Fahrstunden" },
  { id: "exam", name: "Prüfungen" },
  { id: "simulator", name: "Simulatoren" },
  { id: "passThrough", name: "Durchlaufende Posten" },
  { id: "manualInvoice", name: "Manuelle Rechnungen" },
];

function getPerformanceOverviewColumns(config: ColumnConfig): ReactElement[] {
  const { withStudentColumn, withCompensationColumns, withTakeRateColumn, context } = config;

  const fields: ReactElement[] = [
    <DateField key="serviceAt" label="Datum" source="serviceAt" />,
    <PaymentStatusField key="paymentStatus" label={"Zahlungs\u00ADstatus"} source="paymentStatus" size="small" />,
    <ReferenceField key="instructorId" label="Fahrlehrer" reference="instructors" source="instructorId" />,
    ...(withStudentColumn
      ? [
          <ReferenceField
            key="studentId"
            label="Fahrschüler"
            reference="students"
            source="studentId"
            queryOptions={{
              meta: { drivingSchoolId: isDrivingSchool(context) ? context.uid : context.drivingSchoolId },
            }}
          />,
        ]
      : []),
    <TextField key="invoiceNumber" label={"Partner-Rechnungs\u00ADnummer"} source="invoiceNumber" />,
    <TextField key="description" label="Leistung" source="description" />,
    <NumberField key="mainItemQuantity" label="Anzahl" source="mainItemQuantity" />,
    <MoneyField key="mainItemSinglePriceGross" label="Einzelpreis (brutto)" source="mainItemSinglePriceGross" />,
    <MoneyField key="mainItemTotalPriceGross" label="Gesamtpreis (brutto)" source="mainItemTotalPriceGross" />,
    <MoneyField key="totalCreditsAmountUsed" label="Angerechneter Rabatt" source="totalCreditsAmountUsed" hide0 />,
    <MoneyField
      key="prepaidCreditsAmountUsed"
      label="Angerechnete Vorauszahlung"
      source="prepaidCreditsAmountUsed"
      hide0
    />,
    <MoneyField key="invoicePaidAmount" label="Bezahlter Betrag" source="invoicePaidAmount" />,
    <MoneyField key="invoiceTotalGross" label={"Rechnungs\u00ADbetrag (brutto)"} source="invoiceTotalGross" />,
    <PercentageField key="valueAddedTax" label="MwSt." source="valueAddedTax" />,
    <MoneyField key="invoiceTotalNet" label={"Rechnungs\u00ADbetrag (netto)"} source="invoiceTotalNet" />,
    <MoneyField key="applicationFeeAmountNet" label="AUTOVIO-Umsatzanteil (netto)" source="applicationFeeAmountNet" />,
    <MoneyField
      key="applicationFeeAmountGross"
      label="AUTOVIO-Umsatzanteil (brutto)"
      source="applicationFeeAmountGross"
    />,
    <MoneyField
      key="partnerTurnoverAmountNet"
      label="Partner-Umsatzanteil (netto)"
      source="partnerTurnoverAmountNet"
    />,
    <MoneyField
      key="partnerTurnoverAmountGross"
      label="Partner-Umsatzanteil (brutto)"
      source="partnerTurnoverAmountGross"
    />,
    ...(withCompensationColumns
      ? [
          <MoneyField
            key="compensationGross"
            label="Partner-Rabattausgleich (brutto)"
            source="compensationGross"
            hide0
          />,
          <MoneyField key="compensationNet" label="Partner-Rabattausgleich (netto)" source="compensationNet" hide0 />,
        ]
      : []),
    ...(withTakeRateColumn
      ? [<PercentageField key="autovioTakeRate" label="AUTOVIO Take Rate" source="autovioTakeRate" />]
      : []),
  ];

  return fields;
}

const showMonthlyFilterState = atom<boolean>({
  key: "performanceOverviewShowMonthlyFilterState",
  default: true,
});

export type PerformanceOverviewResources = "performanceOverview" | "advancePayments" | "openInvoices";
type PrepaidTypeType = Exclude<
  Parameters<PerformanceOverviewEntryEndpoint["performanceOverviewEntryList"]>[0]["prepaidType"],
  undefined
>;

const prepaidTypeLabels: Record<PrepaidTypeType, string> = {
  CASH: "Bareinzahlung bei Fahrschule",
  EXTERNAL_BANK_TRANSFER: "Überweisung auf Fahrschulkonto",
  STRIPE: "Standard (Klarna, Kreditkarte, Überweisung)",
};

export function PerformanceOverview({
  storeKey,
  resource,
  className,
  context,
  overviewBox,
  listFilter,
}: {
  storeKey: string;
  resource: PerformanceOverviewResources;
  className?: string;
  context: DrivingSchool | Student;
  listFilter?: Partial<Record<keyof PerformanceOverviewRecord, any>>;
  overviewBox?: ReactElement;
}) {
  const [showMonthlyFilter, setShowMonthlyFilter] = useRecoilState(showMonthlyFilterState);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const withMonthFilter = resource !== "openInvoices" && showMonthlyFilter;
  const withPerformanceTypeFilter = resource === "performanceOverview";
  const withPayoutFilter = grants.has("viewPayoutInformation") && resource === "performanceOverview";
  const withStudentColumn = isDrivingSchool(context);
  const withTakeRateColumn = grants.has("viewPerformanceOverviewTakeRate");
  const withCompensationColumns = grants.has("viewPerformanceOverviewCompensation");
  const withLegacyData = grants.has("viewPerformanceOverviewLegacyData");

  const [selectedRecord, setSelectedRecord] = useState<PerformanceOverviewRecord | undefined>(undefined);
  const columns = getPerformanceOverviewColumns({
    withStudentColumn,
    withCompensationColumns,
    withTakeRateColumn,
    context,
  });

  let primaryFilters: ReactElement[] = [];
  let secondaryFilters: ReactElement[] = [];

  if (!context) {
    return <LoadingIndicator />;
  }

  const drivingSchoolId = isDrivingSchool(context) ? context.id : context.drivingSchoolId;

  if (isDrivingSchool(context)) {
    primaryFilters = withMonthFilter
      ? [
          <ServiceTimeFilter
            key="serviceTime"
            source="serviceTime"
            alwaysOn
            minValue={withLegacyData ? "2021-01" : "2023-06"}
          />,
        ]
      : [];
    secondaryFilters = [
      <ReferenceInput
        key="instructor"
        reference="instructors"
        source="instructorId"
        filter={listFilter}
        page={1}
        perPage={999}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <SelectInput label="Fahrlehrer" emptyText={<i>alle Fahrlehrer</i>} />
      </ReferenceInput>,
      <_StudentFilter key="student" source="studentId" drivingSchoolId={drivingSchoolId} alwaysOn />,
      <SelectArrayInput
        key="paymentStatus"
        className="fix-SelectArrayInput-label"
        label="Zahlungsstatus"
        source="paymentStatus"
        choices={Object.entries(PAYMENT_STATUS_LABEL_AND_COLOR).map(([id, { label: name }]) => ({ id, name }))}
        alwaysOn
      />,
      <SelectInput
        key="prepaidType"
        label="Einzahlungsart"
        choices={Object.entries(prepaidTypeLabels).map(([id, name]) => ({ id, name }))}
        emptyText={<i>alle Typen</i>}
        source="prepaidType"
        alwaysOn
      />,
    ];
    if (withPayoutFilter) {
      secondaryFilters.push(
        <ReferenceInput
          key="payout"
          label="Auszahlung"
          source="payout"
          reference="autovioPayouts"
          filter={{ drivingSchoolId: drivingSchoolId }}
          sort={{ field: "payout_date", order: "DESC" }}
          alwaysOn
        >
          <SelectInput
            label="Auszahlung"
            optionText={payoutFilterText}
            onChange={(event) => {
              setShowMonthlyFilter(event.target.value === "");
            }}
          />
        </ReferenceInput>,
      );
    }

    if (withPerformanceTypeFilter) {
      secondaryFilters.push(
        <SelectInput
          key="performanceType"
          label="Art"
          source="performanceType"
          choices={PERFORMANCE_TYPE_CHOICES}
          alwaysOn
        />,
      );
    }
  }

  return (
    <>
      <ResponsiveListSurface variant="compactStandalone" data-testid="PerformanceOverview" sx={{ overflow: "visible" }}>
        <List
          className={className}
          resource={resource || "performanceOverview"}
          storeKey={storeKey}
          title=" " // <-- prevent that the default list title is rendered
          actions={
            <_ListActions
              preferenceKey={storeKey}
              context={context}
              drivingSchoolId={drivingSchoolId}
              resource={resource}
              reactFields={columns}
              secondaryFilters={secondaryFilters}
              isMobile={isMobile}
            />
          }
          filter={listFilter}
          filters={primaryFilters}
          filterDefaultValues={withMonthFilter ? { serviceTime: DateTime.now().toFormat("yyyy-MM") } : {}}
          sort={{ field: "serviceAt", order: "ASC" }}
          empty={<_EmptyState withMonthFilter={withMonthFilter} />}
          sx={{
            boxShadow: "none",
            "& .RaList-main": {
              overflow: "visible !important",
            },
            "& .RaList-content": {
              overflowX: "hidden",
            },
            "& .RaList-main > .RaList-actions": {
              flexWrap: { xs: "wrap !important", sm: "nowrap" },
              mt: { sm: "-5px" },
              maxWidth: { xs: "100%", sm: undefined },
              overflowX: { xs: "visible", sm: "visible" },
              rowGap: 1,
              width: { xs: "100% !important", sm: "fit-content" },
            },
            "& .RaList-actions form, & .RaFilterForm-root": {
              flexBasis: { xs: "100% !important", sm: "auto !important" },
              width: { xs: "100% !important", sm: "auto !important" },
            },
            '& .RaList-actions .filter-field[data-source="serviceTime"]': {
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              width: { xs: "100% !important", sm: "auto !important" },
            },
            '& .RaList-actions .filter-field[data-source="serviceTime"] .RaFilterFormInput-spacer': {
              display: "none",
            },
            '& [data-testid="ServiceTimeFilter"] .MuiFormControl-root': {
              width: "100% !important",
            },
            '& [data-testid="ServiceTimeFilter"]': {
              width: { xs: "100%", sm: "400px", lg: "416px" },
            },
          }}
        >
          <_OverviewBox overviewBox={overviewBox} />
          <PerformanceOverviewDataGrid
            preferenceKey={storeKey}
            columns={columns}
            empty={<_EmptyState withMonthFilter={withMonthFilter} />}
            omit={[
              "invoiceNumber",
              "valueAddedTax",
              "invoiceTotalGross",
              "mainItemQuantity",
              "mainItemSinglePriceGross",
              "applicationFeeAmountGross",
              "partnerTurnoverAmountGross",
              "grossAutovioFee",
              "netAutovioFee",
              ...(withTakeRateColumn ? ["autovioTakeRate"] : []),
              ...(withCompensationColumns ? ["compensationGross", "compensationNet"] : []),
            ]}
            onRecordClick={setSelectedRecord}
          />
        </List>
      </ResponsiveListSurface>
      {selectedRecord && (
        <PerformanceOverviewRecordDialog record={selectedRecord} open onClose={() => setSelectedRecord(undefined)} />
      )}
    </>
  );
}

function _ListActions({
  preferenceKey,
  drivingSchoolId,
  resource,
  context,
  reactFields,
  secondaryFilters,
  isMobile,
}: {
  preferenceKey: string;
  drivingSchoolId: string;
  resource: PerformanceOverviewResources;
  context: DrivingSchool | Student;
  reactFields: ReactElement[];
  secondaryFilters: ReactElement[];
  isMobile: boolean;
}) {
  const { filterValues } = useListFilterContext();
  const { dialogProps, openDialog } = useDialog();
  const { anchorEl, open: menuOpen, openMenu, closeMenu } = useMenu();

  const withExportDialog =
    ((!!filterValues.serviceTime && !filterValues.payout) || resource === "openInvoices") &&
    grants.has("viewPerformanceOverviewDownloadReport");

  const withExcelExport = grants.has("viewPerformanceOverviewExcelExport") && isDrivingSchool(context);

  // Read current visible columns from preferences of DatagridConfigurable
  const finalPreferenceKey = preferenceKey;
  const [availableColumns] = useStore<Array<{ source: string; label: string; index: string | number }>>(
    "preferences." + finalPreferenceKey + ".availableColumns",
    [],
  );
  const [omit] = useStore<Array<string> | undefined>("preferences." + finalPreferenceKey + ".omit", []);
  const resolvedAvailableColumns =
    availableColumns && availableColumns.length > 0
      ? availableColumns
      : (reactFields || []).map((field, idx) => ({
          source: field.props.source,
          label: field.props.label,
          index: String(idx),
        }));
  const defaultColumns = resolvedAvailableColumns
    .filter((column) => !omit?.includes(column.source))
    .map((column) => column.index);
  const [columnsPref] = useStore<Array<string | number>>(
    "preferences." + finalPreferenceKey + ".columns",
    defaultColumns,
  );
  const columnMappings = resolvedAvailableColumns
    .filter((col) => columnsPref.includes(col.index))
    .map(({ source, label }) => ({ field: source, label }));

  const excelExportButton = withExcelExport ? (
    <ExcelExportButton
      filename={
        resource === "performanceOverview"
          ? "Leistungsübersicht"
          : resource === "advancePayments"
            ? "Vorauszahlungen"
            : "Offene Posten"
      }
      sheetName={
        resource === "performanceOverview"
          ? "Leistungsübersicht"
          : resource === "advancePayments"
            ? "Vorauszahlungen"
            : "Offene Posten"
      }
      onlyMappedColumns={true}
      columnMappings={columnMappings}
      includeSummary={true}
      menuItem={isMobile}
      onAction={isMobile ? closeMenu : undefined}
      valueFormatters={{
        serviceAt: (value) => (value ? new Date(value).toLocaleDateString("de-DE") : ""),
        paymentStatus: (value) =>
          PAYMENT_STATUS_LABEL_AND_COLOR[value as keyof typeof PAYMENT_STATUS_LABEL_AND_COLOR]?.label || value,
        instructorId: (value, record) => record.rawEntry?.instructor_name || value,
        studentId: (value, record) => record.rawEntry?.student_name || value,
        autovioTakeRate: (value) => (value ? formatPercentage(value) : ""),
      }}
      fetchData={async (filterValues, sort) => {
        if (!isDrivingSchool(context)) {
          throw new Error("Context is not a driving school");
        }
        const drivingSchoolId = context.id;

        const { data } = await performanceOverviewProvider.getList(resource, {
          pagination: { page: 1, perPage: 10000 },
          sort: sort || { field: "serviceAt", order: "ASC" },
          filter: {
            drivingSchoolId: drivingSchoolId,
            serviceTime: filterValues.serviceTime,
            payout: filterValues.payout,
            instructorId: filterValues.instructorId,
            studentId: filterValues.studentId,
            paymentStatus: filterValues.paymentStatus,
            prepaidType: filterValues.prepaidType,
            performanceType: filterValues.performanceType,
          },
        });

        return data;
      }}
    />
  ) : null;

  return (
    <TopToolbar
      sx={{
        alignItems: "center",
        flexGrow: { xs: 0, sm: 1 },
        flexWrap: { xs: "wrap !important", sm: "nowrap" },
        gap: { xs: 0.5, sm: 0 },
        justifyContent: "flex-start",
        width: { xs: "100%", sm: "auto" },
        "& > *": {
          marginLeft: { xs: "0 !important", sm: undefined },
        },
        "& > button:first-of-type": {
          marginLeft: { xs: "0 !important", sm: "16px !important" },
          top: { xs: 0, sm: "3px" },
        },
        "& > .MuiBox-root ~ button": {
          marginLeft: { xs: "0 !important", sm: "8px !important" },
        },
        '& > button[aria-label="Weitere Aktionen"]': {
          marginLeft: { xs: "auto !important", sm: undefined },
        },
        '& > button[aria-label="Export"], & > .MuiLoadingButton-root': {
          height: "32px",
        },
      }}
    >
      {secondaryFilters.length > 0 && <MoreFiltersButton filters={secondaryFilters} />}
      <Box sx={{ display: { xs: "none", sm: "block" }, flex: "1 1 auto" }} />
      {isMobile ? (
        <>
          {(withExportDialog || withExcelExport) && (
            <Tooltip title="Weitere Aktionen">
              <IconButton aria-label="Weitere Aktionen" size="small" onClick={openMenu}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          )}
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
            {withExportDialog && isDrivingSchool(context) && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  openDialog();
                }}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                Export
              </MenuItem>
            )}
            {excelExportButton}
          </Menu>
        </>
      ) : (
        <>
          {grants.has("viewPerformanceOverviewColumnSelection") && (
            <SelectColumnsButton preferenceKey={preferenceKey} />
          )}
          {withExportDialog && isDrivingSchool(context) && (
            <Button variant="outlined" label="Export" onClick={openDialog}>
              <DownloadIcon />
            </Button>
          )}
          {excelExportButton}
        </>
      )}
      {withExportDialog && isDrivingSchool(context) && (
        <PerformanceOverviewReportExportDialog
          drivingSchoolId={drivingSchoolId}
          serviceTime={filterValues.serviceTime}
          resource={resource}
          {...dialogProps}
        />
      )}
    </TopToolbar>
  );
}

function _OverviewBox({ overviewBox }: { overviewBox?: ReactElement }) {
  const { filterValues } = useListFilterContext();
  if (filterValues.payout) {
    // Don't render the overview box when a payout is selected to prevent confusion:
    // The total turnover displayed in the overview box would probably not match the
    // payout amount because of refunds, credits, etc.
    return (
      <Fade in={true}>
        <Alert severity="warning" style={{ margin: "20px" }}>
          <strong>Achtung</strong>: Deine Leistungsübersicht ist auf eine Auszahlung limitiert. Du siehst nur noch
          Leistungen, die in der ausgewählten Auszahlung abgerechnet wurden.
        </Alert>
      </Fade>
    );
  }
  return overviewBox;
}

function _EmptyState({ withMonthFilter }: { withMonthFilter: boolean }) {
  const { filterValues, displayedFilters, setFilters } = useListFilterContext();

  useEffect(() => {
    if (!withMonthFilter) {
      return;
    }
    if (!filterValues.serviceTime) {
      setFilters({ ...filterValues, serviceTime: DateTime.now().toFormat("yyyy-MM") }, displayedFilters);
    }
  }, [withMonthFilter, filterValues.serviceTime]);

  if (!filterValues.serviceTime) {
    return null;
  }

  return <EmptyState noEntriesText="Keine Einträge." />;
}

function payoutFilterText(payout: AutovioPayoutRecord) {
  const date = payout.payoutDate.toLocaleString(DateTime.DATE_SHORT, {
    locale: "de",
  });
  return `${date} (${formatMoney(payout.calculatedPayoutAmount)})`;
}

function _StudentFilter(props: { drivingSchoolId: string; source: "studentId"; alwaysOn: boolean }) {
  const { drivingSchoolId, source, alwaysOn } = props;
  const { setValue } = useFormContext();
  return (
    <ReferenceInput filter={{ drivingSchoolId }} source={source} reference="students" alwaysOn={alwaysOn}>
      <AutocompleteInput
        label="Fahrschüler"
        sx={{
          "& .MuiInputBase-root": { py: "0" },
          "& .MuiFormLabel-root": {
            transform: "translate(14px, 8px) scale(1)",
          },
          "& .MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        }}
        onChange={(studentId) => {
          if (studentId) {
            setValue("studentId", studentId);
            setValue("serviceTime", undefined);
            return;
          }
          setValue("serviceTime", DateTime.now().toFormat("yyyy-MM"));
        }}
      />
    </ReferenceInput>
  );
}
